// The one producer of the app's events in an account's invalidation log
// (cache.ts): a pull request the viewer opened, a notice that named one,
// and each write the app makes to one (an intent before the write is sent,
// done once it is answered). Whatever keeps data about a pull request
// hears the events from the log, in this tab and the others, not from
// whoever made them.
//
// An event says what changed (`changeOf`): the pull request as GitHub
// shows it (its subject is `prKeyOf`), or the viewer's review marks on it
// (`marksKeyOf`: a subject of their own, so nothing kept for the pull
// request is dropped by a mark). A listener asks `changeOf` and takes only
// the changes it depends on.
//
// The log opens once the account is known. Events recorded before then
// wait, the latest per pull request, and are recorded in the first log
// given; `onWaiting` hears their subjects meanwhile, for what is kept in
// this page. An event the log could not keep (not stored, or only in this
// page's memory) is told to `onLost`'s listeners: a page opened later
// would not know of it, so what is stored for the pull request must go.
import { onIdentityChange } from '../api/identity';
import type { InvalidationLog, InvalidationSource, WriteKind, WriteResult } from './cache';

/** A pull request's subject in the log: `owner/name#number`, lowercased. */
export const prKeyOf = (repo: string, number: number) => `${repo}#${number}`.toLowerCase();

/** The subject of the viewer's review marks on a pull request. */
export const marksKeyOf = (repo: string, number: number) => `marks:${prKeyOf(repo, number)}`;

/** A write to the pull request itself (every kind but the review marks). */
export type PrWriteKind = Exclude<WriteKind, 'marks'>;

/**
 * What an event changed: the pull request as GitHub shows it, the viewer's
 * review marks on it, or `unknown` (another build's event this one cannot
 * read): a listener treats that as a change to whatever it keeps for the
 * subject.
 */
export type Change = 'pull-request' | 'marks' | 'unknown';

/** What `e` changed. Every source and kind is named, so a new one does not compile until it is placed. */
export function changeOf(e: InvalidationSource): Change {
  switch (e.source) {
    case 'opened':
    case 'notice':
      return 'pull-request';
    case 'unknown':
      return 'unknown';
    case 'app': {
      const kind = e.kind;
      switch (kind) {
        case 'publish':
        case 'resolve':
        case 'review':
        case 'reply':
        case 'rerequest':
          return 'pull-request';
        case 'marks':
          return 'marks';
        default:
          return unknownKind(kind);
      }
    }
    default:
      return unknownKind(e);
  }
}

function unknownKind(x: never): never {
  throw new TypeError(`an invalidation this build does not know: ${JSON.stringify(x)}`);
}

/** Events waiting for a log, at most this many pull requests' (the oldest go first). */
export const MAX_WAITING = 500;

export class AppLog {
  private log: InvalidationLog | null = null;
  /** Events recorded while no log was given, the latest per subject. */
  private waiting = new Map<string, InvalidationSource>();
  private lost = new Set<(subject: string) => void>();
  private waits = new Set<(subject: string, source: InvalidationSource) => void>();
  /** Records begun and not yet over (see `settled`). */
  private inFlight = new Set<Promise<unknown>>();

  /** Record in `log` from now on, starting with the events waiting for one; null: none, and events wait again. */
  use(log: InvalidationLog | null): void {
    this.log = log;
    if (!log) return;
    const waiting = [...this.waiting];
    this.waiting.clear();
    for (const [subject, source] of waiting) this.track(log.record(source, subject).then((r) => this.kept(subject, r)));
  }

  /**
   * Resolves once every event begun so far has been recorded, or failed to
   * be (and `onLost` was told), with those begun meanwhile. For tests, and
   * for whatever must know the log has heard all this producer made.
   */
  async settled(): Promise<void> {
    while (this.inFlight.size) await Promise.allSettled([...this.inFlight]);
  }

  /** Forget the events waiting for a log, and the log: another account signed in. */
  clear(): void {
    this.log = null;
    this.waiting.clear();
  }

  /** Hear each subject whose event the log could not keep; returns the unsubscribe. */
  onLost(listener: (subject: string) => void): () => void {
    this.lost.add(listener);
    return () => this.lost.delete(listener);
  }

  /** Hear each event made while no log is given, with its subject (the log's own listeners hear the rest). */
  onWaiting(listener: (subject: string, source: InvalidationSource) => void): () => void {
    this.waits.add(listener);
    return () => this.waits.delete(listener);
  }

  /** Record `source` for pull request `repo#number`; resolves to whether the log kept it (true while it waits for one). */
  async record(source: InvalidationSource, repo: string, number: number): Promise<boolean> {
    const subject = prKeyOf(repo, number);
    if (!this.log) {
      this.wait(subject, source);
      return true;
    }
    return this.kept(subject, await this.log.record(source, subject));
  }

  /** The viewer opened pull request `repo#number`: what is kept of it is read afresh. */
  opened(repo: string, number: number): void {
    this.track(this.record({ source: 'opened' }, repo, number));
  }

  /**
   * The app is about to send a write of `kind` to pull request
   * `repo#number`: recorded now as an intent; call the returned function
   * once the write is answered, or failed (it may have gone through), to
   * record it done. The intent is applied and sent to the other tabs at
   * once; its write to storage is begun, not awaited, before the write
   * goes out. Before a log is given the intent waits for one, and the done
   * is recorded (or waits) only when the write is answered, never sooner.
   */
  writing(repo: string, number: number, kind: PrWriteKind): () => void {
    return this.write(prKeyOf(repo, number), kind);
  }

  /** The app is about to save review marks for pull request `repo#number`: as `writing`, under the marks' own subject. */
  marking(repo: string, number: number): () => void {
    return this.write(marksKeyOf(repo, number), 'marks');
  }

  private write(subject: string, kind: WriteKind): () => void {
    const log = this.log;
    if (!log) {
      this.wait(subject, { source: 'app', kind, phase: 'intent' });
      return () => {
        const done: InvalidationSource = { source: 'app', kind, phase: 'done' };
        // A log given meanwhile recorded the intent; the done goes to it now.
        if (this.log) this.track(this.log.record(done, subject).then((r) => this.kept(subject, r)));
        else this.wait(subject, done);
      };
    }
    const intent = log.intend(kind, subject).then((i) => (this.kept(subject, i.stored), i));
    this.track(intent);
    return () => this.track(intent.then(async (i) => this.kept(subject, await i.done())));
  }

  /** Count `p` in flight until it settles; a rejection still goes unhandled, as it would untracked. */
  private track(p: Promise<unknown>): void {
    const t: Promise<unknown> = p.finally(() => this.inFlight.delete(t));
    this.inFlight.add(t);
  }

  private wait(subject: string, source: InvalidationSource) {
    this.waiting.delete(subject);
    this.waiting.set(subject, source);
    if (this.waiting.size > MAX_WAITING) {
      // No room: the oldest is never recorded, so what is stored for it must go.
      const oldest = this.waiting.keys().next().value!;
      this.waiting.delete(oldest);
      for (const l of [...this.lost]) l(oldest);
    }
    for (const l of [...this.waits]) l(subject, source);
  }

  /** Whether `stored` outlives the page; tells `onLost` when not. */
  private kept(subject: string, stored: WriteResult): boolean {
    if (stored.ok && stored.durable) return true;
    for (const l of [...this.lost]) l(subject);
    return false;
  }
}

/** The app's producer, for the signed-in account (state/dashCache.ts gives it the account's log). */
export const appLog = new AppLog();
onIdentityChange(() => appLog.clear());
